// ChatTab — primary chat interaction surface (Phase 3E + 3F + 3I).
//
// Reworked from the legacy ChatPanel: standard chat thread (user right,
// system/assistant left), input bar with attach + text + send, drag-and-drop
// zone, system messages via MarkdownRenderer. Ports back the old ChatPanel
// affordances the initial rewrite dropped: scope selector (all/documents/public),
// model-path selector (auto/private/public), up-arrow history recall,
// save-response-to-case, Evaluate vs public data, citation rendering with
// type-specific icons, plus a Run-review button cluster in the header
// (reusing handlers lifted into CaseDetailPane). Keeps the new features:
// attachments, transparency/preview-send, MarkdownRenderer, deficiency /
// whitewash / compare job hooks.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Paperclip,
  Send,
  Loader2,
  AlertTriangle,
  X,
  FileText,
  Download,
  Copy,
  Bookmark,
  Database,
  Shield,
  MessageSquare,
  RefreshCw,
  Play,
  Ban,
  AlertCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  loadChatHistory,
  saveChatResponse,
  evaluateVsPublic,
  downloadChatDocx,
  type ChatMessage,
  type ChatScope,
  type ChatModelPath,
  type ChatCitation,
  type EvalDiscrepancy,
} from '../api/chat';
import {
  submitAiChat,
  submitDocDrop,
  listCaseRequests,
  getRequest,
  type CaseRequest,
  type CaseRequestAiChatPayload,
  type CaseRequestAiChatResponse,
  type CaseRequestDocDropResponse,
  type CaseRequestDocReconstructResponse,
  type CaseRequestDocPayload,
} from '../api/caseRequests';
import { triggerPublicLookup, getPublicDataRun } from '../api/review';
import { useToastStore } from '../stores/toast';
import {
  type Case,
  type DocumentItem,
} from '../api/cases';
import {
  reconstruct,
} from '../api/datawash';

// Placeholder pattern from the tag_whitewash system (REDACTED-<key>).
const PLACEHOLDER_RE = /\bREDACTED-([a-z0-9]+)\b/g;
function hasPiiTags(text: string): boolean {
  PLACEHOLDER_RE.lastIndex = 0;
  return PLACEHOLDER_RE.test(text);
}
import MarkdownRenderer from './MarkdownRenderer';

interface ChatTabProps {
  caseData: Case;
  /** Notifies parent of job ids so other tabs can poll/fetch results. */
  onWhitewashJob?: (jobId: string) => void;
  onDeficiencyJob?: (jobId: string) => void;
  onCompareJob?: (jobId: string, summary?: string, originalFilename?: string, revisionFilenames?: string[], originalDocId?: string, revisionDocId?: string) => void;
  onDocumentsChanged?: () => void;
  /** Parent can push an extra system message (e.g. from a compare result
   *  or a "Review completed" notice). */
  externalMessage?: ChatMessage | null;
  externalSeq?: number;
  /** Run-review handlers + state (lifted into CaseDetailPane so the same
   *  handlers power the Overview + Chat Run-review buttons). */
  onRunReview?: () => void;
  onReReview?: () => void;
  onCancelReview?: () => void;
  reviewLoading?: boolean;
  rereviewLoading?: boolean;
  reviewInProgress?: boolean;
  cancellingReview?: boolean;
  hasReview?: boolean;
}

const SCOPE_OPTIONS: { value: ChatScope; label: string; note: string }[] = [
  {
    value: 'all',
    label: 'All',
    note: 'Includes private documents -> uses the local AI',
  },
  {
    value: 'documents',
    label: 'Documents',
    note: 'Private documents -> uses the local AI',
  },
  {
    value: 'public',
    label: 'Public',
    note: 'Public information only -> can use the cloud AI',
  },
];

const MODEL_PATH_OPTIONS: { value: ChatModelPath; label: string; note: string }[] = [
  {
    value: 'auto',
    label: 'Auto',
    note: 'Model chosen by scope (docs -> local, public -> cloud)',
  },
  {
    value: 'private',
    label: 'Local',
    note: 'Force the local AI',
  },
  {
    value: 'public',
    label: 'Cloud',
    note: 'Force the cloud AI. Not available if the scope includes private documents unless PII redaction is on.',
  },
];

function citationIcon(type: ChatCitation['type']) {
  if (type === 'document') return <FileText className="h-3 w-3" />;
  if (type === 'public') return <Database className="h-3 w-3" />;
  return <MessageSquare className="h-3 w-3" />;
}

function discrepanciesBlock(discrepancies: EvalDiscrepancy[], summary: string): string {
  const header = `Discrepancies found: ${discrepancies.length}`;
  const lines = discrepancies.map(
    (d) =>
      `- Claim: ${d.claim || '(unspecified)'}\n` +
      `  Public value: ${d.public_value || '(unspecified)'}\n` +
      `  Source: ${d.source_citation || '(unspecified)'}\n` +
      `  Severity: ${d.severity_note || '(unspecified)'}`,
  );
  return [header, summary, ...lines].filter(Boolean).join('\n\n');
}

const POLL_INTERVAL = 2000;

// ---- case-request ledger: convert a durable CaseRequest row into the
// ChatMessage pair (user prompt/filename + assistant/system result) ----
function docFilenameOf(r: CaseRequest): string {
  const p = r.request_payload as CaseRequestDocPayload;
  return p?.filename || 'file';
}
function requestToMessages(r: CaseRequest): ChatMessage[] {
  if (r.kind === 'ai_chat') {
    const p = r.request_payload as CaseRequestAiChatPayload;
    const prompt = p?.prompt || '(empty)';
    const scope = p?.scope;
    if (r.status === 'pending') {
      return [
        { role: 'user', content: prompt, scope },
        { role: 'assistant', content: '', request_id: r.id, _pending: true, _pendingKind: 'ai_chat' },
      ];
    }
    if (r.status === 'error') {
      return [
        { role: 'user', content: prompt, scope },
        { role: 'system', content: `Send failed: ${r.error || 'unknown error'}`, request_id: r.id },
      ];
    }
    const resp = (r.response_payload || {}) as CaseRequestAiChatResponse;
    return [
      { role: 'user', content: prompt, scope },
      {
        role: 'assistant',
        content: resp.answer || '(empty response)',
        citations: resp.citations,
        path_used: resp.path_used ?? null,
        model_used: resp.model_used ?? null,
        run_id: resp.run_id ?? null,
        scope,
        request_id: r.id,
      },
    ];
  }
  // doc_drop | doc_reconstruct
  const filename = docFilenameOf(r);
  if (r.status === 'pending') {
    const label = r.kind === 'doc_reconstruct' ? `Reconstructing ${filename}...` : `Uploading ${filename}...`;
    return [
      { role: 'user', content: `Dropped ${filename}` },
      { role: 'system', content: label, request_id: r.id, _pending: true, _pendingKind: r.kind === 'doc_reconstruct' ? 'doc_reconstruct' : 'doc_drop' },
    ];
  }
  if (r.status === 'error') {
    return [
      { role: 'user', content: `Dropped ${filename}` },
      { role: 'system', content: `Upload failed: ${r.error || 'unknown error'}`, request_id: r.id },
    ];
  }
  const resp = (r.response_payload || {}) as CaseRequestDocDropResponse & CaseRequestDocReconstructResponse;
  if (r.kind === 'doc_reconstruct') {
    const text = resp.reconstructed_text || '(reconstruction yielded empty text)';
    return [
      { role: 'user', content: `Dropped ${filename}` },
      {
        role: 'system',
        content: `**${filename}**\n\n${text}`,
        reconstructed: true,
        unknown_tags: resp.unknown_tags,
        warnings: resp.warnings,
        request_id: r.id,
      },
    ];
  }
  // doc_drop complete
  const count = resp.doc_count || resp.doc_ids?.length || 1;
  return [
    { role: 'user', content: `Dropped ${filename}` },
    { role: 'system', content: `Upload complete. ${count} document(s) ready. Ask a question to start the review.`, request_id: r.id },
  ];
}

export default function ChatTab({
  caseData,
  onWhitewashJob: _onWhitewashJob,
  onDeficiencyJob: _onDeficiencyJob,
  onDocumentsChanged,
  externalMessage,
  externalSeq,
  onRunReview,
  onReReview,
  onCancelReview,
  reviewLoading,
  rereviewLoading,
  reviewInProgress,
  cancellingReview,
  hasReview,
}: ChatTabProps) {
  const { addToast } = useToastStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Drag-and-drop is scoped to a small isolated drop-zone wrapper around
  // the input bar (NOT the chat thread). A drag enter/leave counter keeps
  // `isDragging` stable across child-boundary toggles so the message list
  // never re-renders on drag state changes (fixes dir-057 flicker). Mirrors
  // the DocumentsTab drop-zone pattern.
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [pendingAttachments, setPendingAttachments] = useState<DocumentItem[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  // Per-message data-sources panel open state. Collapsed by default (empty)
  // so the citation chips are hidden until the user clicks "Data sources (N)".
  const [sourcesOpen, setSourcesOpen] = useState<Record<number, boolean>>({});
  const [exportingDocx, setExportingDocx] = useState(false);
  // Scope + model-path selectors (ported from ChatPanel).
  const [scope, setScope] = useState<ChatScope>('all');
  const [modelPath, setModelPath] = useState<ChatModelPath>('auto');
  // Evaluate-vs-public-data progress state (ported from ChatPanel).
  const [evaluating, setEvaluating] = useState(false);
  const [evalPhase, setEvalPhase] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // textarea ref for auto-resize + up-arrow recall caret placement.
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // case-request ledger: poll-abort flag. Set true on unmount / case
  // switch so in-flight poll loops exit cleanly instead of touching state
  // after the component is gone.
  const pollAbort = useRef(false);
  // Ref mirror of onDocumentsChanged so poll loops (long-lived, started in
  // effects) always call the latest prop without re-creating the loop.
  const onDocumentsChangedRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => { onDocumentsChangedRef.current = onDocumentsChanged; }, [onDocumentsChanged]);
  // External-message dedupe: track the last externalSeq consumed so a
  // remount of ChatTab (tab switch away + back) does not replay the same
  // system message. -1 = none consumed yet.
  const lastExternalSeq = useRef<number>(-1);

  // Up-arrow history recall (readline-style). inputHistory is derived
  // from per-case `messages`; recallIndex is null when not recalling,
  // otherwise an index into inputHistory (oldest=0). draftRef holds the
  // in-progress draft saved when recall starts so ArrowDown past newest /
  // Esc can restore it.
  const inputHistory = useMemo(
    () => messages.filter((m) => m.role === 'user').map((m) => m.content),
    [messages],
  );
  const recallIndex = useRef<number | null>(null);
  const draftRef = useRef('');
  const activeScopeNote = SCOPE_OPTIONS.find((o) => o.value === scope)?.note;

  // Export an assistant/system message as a .md file (ports ChatPanel.handleExport).
  const handleExport = useCallback((idx: number) => {
    const m = messages[idx];
    if (!m) return;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `${pad(now.getHours())}${pad(now.getMinutes())}`;
    const slug = (caseData.case_number || caseData.id).replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `chat_${slug}_${ts}.md`;
    const header =
      `# Chat response\n\n` +
      `Case: ${caseData.case_number || caseData.id}\n` +
      (m.model_used ? `Model: ${m.model_used}\n` : '') +
      (m.path_used ? `Path: ${m.path_used}\n` : '') +
      `Saved: ${now.toISOString()}\n\n---\n\n`;
    const blob = new Blob([header + m.content + '\n'], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [messages, caseData.case_number, caseData.id]);

  // Export the WHOLE conversation as a Word (.docx) doc via the backend.
  // Mirrors downloadReviewRecordDocx's blob-download pattern.
  const handleExportDocx = useCallback(async () => {
    if (exportingDocx) return;
    setExportingDocx(true);
    try {
      await downloadChatDocx(caseData.id, caseData.case_number || caseData.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('chat .docx export failed', err);
    } finally {
      setExportingDocx(false);
    }
  }, [exportingDocx, caseData.id, caseData.case_number]);

  // Copy an assistant/system message's raw content to the clipboard.
  const handleCopy = useCallback(async (idx: number) => {
    const m = messages[idx];
    if (!m) return;
    try {
      await navigator.clipboard.writeText(m.content);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 2000);
    } catch { /* clipboard may be blocked */ }
  }, [messages]);

  // ---- case-request ledger: poll a pending request until it lands, then
  // swap the placeholder bubble for the real answer/result. Backoff 2s ->
  // 5s after 60s -> 15s after 5min; cap 10min. Exits cleanly on case
  // switch / unmount via pollAbort. ----
  const finalizeRequest = useCallback((r: CaseRequest) => {
    if (pollAbort.current) return;
    const pair = requestToMessages(r);
    // The pair is [userMsg, resultMsg]; only the placeholder (the message
    // carrying request_id === r.id AND _pending) should be replaced.
    const resultMsg = pair[1];
    if (!resultMsg) return;
    setMessages((prev) => prev.map((m) => (m.request_id === r.id ? resultMsg : m)));
    // Refresh the Documents tab when a regular upload lands.
    if (r.kind === 'doc_drop' && r.status === 'complete') {
      onDocumentsChangedRef.current?.();
    }
  }, []);

  const pollLoop = useCallback(async (requestId: string) => {
    const start = Date.now();
    let delay = POLL_INTERVAL;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((res) => setTimeout(res, delay));
      if (pollAbort.current) return;
      let r: CaseRequest;
      try {
        r = await getRequest(requestId);
      } catch {
        return; // transport error; leave the placeholder in place
      }
      if (pollAbort.current) return;
      if (r.status !== 'pending') {
        finalizeRequest(r);
        return;
      }
      const elapsed = Date.now() - start;
      if (elapsed >= 10 * 60 * 1000) {
        finalizeRequest({ ...r, status: 'error', error: 'request timed out (pending after 10min)' });
        return;
      }
      delay = elapsed > 5 * 60 * 1000 ? 15000 : elapsed > 60 * 1000 ? 5000 : POLL_INTERVAL;
    }
  }, [finalizeRequest]);

  // Load conversation on case open. PRIMARY source = the durable
  // case_request ledger (listCaseRequests). Falls back to the legacy
  // audit-log chat history only when the ledger has no rows for this
  // case (pre-migration cases). Pending ledger rows re-establish poll
  // loops so wait-for-answer state survives tab/case switches + reloads.
  useEffect(() => {
    setLoading(true);
    setError(null);
    // reset recall state when switching cases so stale recall index / draft
    // from the prior case do not leak in.
    recallIndex.current = null;
    draftRef.current = '';
    setInput('');
    abortRef.current?.abort();
    pollAbort.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    let cancelled = false;
    (async () => {
      try {
        const ledger = await listCaseRequests(caseData.id, { include: 'pending,recent', limit: 50 });
        if (cancelled || ac.signal.aborted) return;
        const rows = ledger.requests || [];
        if (rows.length === 0) {
          // Pre-migration case: show legacy audit-log chat history.
          const legacy = await loadChatHistory(caseData.id);
          if (cancelled || ac.signal.aborted) return;
          setMessages(legacy);
          return;
        }
        // Convert ledger rows -> ChatMessage pairs (oldest-first).
        const msgs: ChatMessage[] = [];
        for (const r of rows) msgs.push(...requestToMessages(r));
        setMessages(msgs);
        // Re-establish polling for any still-pending rows.
        for (const r of rows) {
          if (r.status === 'pending') pollLoop(r.id);
        }
      } catch (e: any) {
        if (cancelled || ac.signal.aborted) return;
        // Ledger endpoint unavailable -> fall back to legacy history.
        try {
          const legacy = await loadChatHistory(caseData.id);
          if (cancelled || ac.signal.aborted) return;
          setMessages(legacy);
        } catch (e2: any) {
          if (!cancelled && !ac.signal.aborted) setError(e2?.response?.data?.detail || 'Failed to load chat');
        }
      } finally {
        if (!cancelled && !ac.signal.aborted) setLoading(false);
      }
    })();
    return () => { cancelled = true; ac.abort(); pollAbort.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData.id]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, evaluating]);

  // Auto-resize the textarea to fit content up to max-h-32 (8rem). Re-runs
  // on every input change so the box grows with the message.
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxH = 128; // px, matches max-h-32
    ta.style.height = `${Math.min(ta.scrollHeight, maxH)}px`;
  }, [input]);

  // External message injection (e.g. compare result, "Review completed"
  // notice from CaseDetailPane). Dedupes on externalSeq so a tab switch
  // away + back (remount) does not replay the same message. On mount we
  // seed lastExternalSeq to the current externalSeq so only subsequent
  // bumps push a new message.
  useEffect(() => {
    lastExternalSeq.current = externalSeq ?? -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (externalMessage && externalSeq !== undefined && externalSeq !== lastExternalSeq.current) {
      lastExternalSeq.current = externalSeq;
      setMessages((prev) => [...prev, externalMessage]);
    } else if (externalMessage && externalSeq === undefined) {
      // Legacy callers that do not bump externalSeq: push once per object
      // identity change (best-effort, may replay on remount).
      setMessages((prev) => [...prev, externalMessage]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMessage, externalSeq]);

  const pushSystem = useCallback((content: string, extra?: Partial<ChatMessage>) => {
    setMessages((prev) => [...prev, { role: 'system', content, ...extra }]);
  }, []);

  const pushUser = useCallback((content: string, attachments?: string[]) => {
    setMessages((prev) => [...prev, { role: 'user', content, attachments }]);
  }, []);

  // Upload files via drag-and-drop or attach through the DURABLE
  // case-request ledger (POST /cases/{id}/requests/doc-drop). One row
  // per file is created server-side; the server detects PII-tagged files
  // (REDACTED-<key> tokens) and reconstructs them in place, or uploads
  // regular files as case documents. The browser cannot read
  // FlateDecode-compressed PDF tokens, so detection MUST be server-side.
  // Each file gets its own pending placeholder + poll loop so the user
  // sees exactly what happened to each file, and state survives tab /
  // case switches + reloads.
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setError(null);
    setSending(true);
    try {
      const { request_ids } = await submitDocDrop(caseData.id, fileArr);
      if (pollAbort.current) return;
      // request_ids come back in the same order as the uploaded files.
      for (let i = 0; i < request_ids.length; i++) {
        const rid = request_ids[i];
        const fname = fileArr[i]?.name || 'file';
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: `Dropped ${fname}` },
          { role: 'system', content: `Processing ${fname}...`, request_id: rid, _pending: true, _pendingKind: 'doc_drop' },
        ]);
        pollLoop(rid);
      }
    } catch (e: any) {
      pushSystem('Upload failed: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
    } finally {
      setSending(false);
    }
  }, [caseData.id, pollLoop, pushSystem]);

  // Drag-and-drop handlers scoped to the input-bar drop zone. Enter/leave
  // counter prevents the classic child-boundary flicker (dragleave fires
  // when crossing into a child element). Reset on drop / drag end.
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // Stop bubbling so a drop on the input-bar drop zone is not re-handled
    // by the outer chat-tab drop delegate (dir-063). Without this, handleFiles
    // would fire twice (input-bar handler + outer delegate) and double-upload.
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  // Drop delegate on the outer chat container. The input-bar drop zone
  // (dir-057) only covers the bottom input bar; without preventDefault on
  // dragover across the rest of the chat area (message list, header), the
  // browser treats a file drop as file-open navigation and our drop handler
  // never runs. This delegate calls preventDefault on every dragover inside
  // the chat tab (via bubbling) and routes drops to the same onDrop handler.
  // It touches NO state on dragover, so the message list does not re-render
  // and the dir-057 flicker fix is preserved. The input-bar drop zone still
  // owns the dragCounter-driven isDragging highlight; drops on it stop
  // propagation so this delegate does not double-handle.
  const onChatDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDragEnd = () => {
    dragCounter.current = 0;
    setIsDragging(false);
  };

  const onAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
    e.target.value = '';
  };

  // Send a chat message. If the backend signals an external-AI path is
  // needed (via a sentinel in the response or a dedicated field), we first
  // fire preview-send and show the transparency modal.
  const onSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    // reset recall to "newest" after a send.
    recallIndex.current = null;
    draftRef.current = '';
    setSending(true);
    setError(null);
    // If the pasted/typed text contains PII placeholders (REDACTED-<key>),
    // reconstruct it to real PII + display as a system message instead of
    // sending to the LLM.
    if (hasPiiTags(text)) {
      pushUser(text, []);
      try {
        const result = await reconstruct(caseData.id, text);
        pushSystem(
          result.reconstructed_text || '(reconstruction yielded empty text)',
          { reconstructed: true, unknown_tags: result.unknown_tags }
        );
      } catch (e: any) {
        pushSystem('Reconstruct failed: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
      } finally {
        setSending(false);
      }
      return;
    }
    pushUser(text, pendingAttachments.map((d) => d.id));
    setPendingAttachments([]);
    try {
      // Durable async path: insert a pending case_request row, render a
      // placeholder assistant bubble tied to its request_id, then poll
      // until it lands. The "waiting for AI" state is now a DB row, so
      // it survives tab/case switches + reloads.
      const { request_id } = await submitAiChat(caseData.id, { prompt: text, scope, model_path: modelPath });
      if (pollAbort.current) return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', request_id, _pending: true, _pendingKind: 'ai_chat' },
      ]);
      pollLoop(request_id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Send failed');
      pushSystem('Send failed: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
      return;
    }
    // readline-style up/down-arrow recall of previously sent user messages.
    // ArrowUp recalls the previous (older) sent message; ArrowDown moves
    // forward (newer); Esc cancels recall + restores the saved draft.
    // ArrowUp only triggers recall when the cursor is at the very start of
    // the input so pressing Up inside a multiline message still moves the
    // caret up a line. When recall starts we save the in-progress draft.
    const ta = e.currentTarget;
    if (e.key === 'ArrowUp') {
      const atStart = ta.selectionStart === 0 && ta.selectionEnd === 0;
      if (!atStart || inputHistory.length === 0) return; // let caret move
      e.preventDefault();
      if (recallIndex.current === null) {
        draftRef.current = input; // save draft being typed
        recallIndex.current = inputHistory.length - 1;
      } else if (recallIndex.current > 0) {
        recallIndex.current -= 1;
      } else {
        return; // already at oldest; keep showing it
      }
      setInput(inputHistory[recallIndex.current]);
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      });
      return;
    }
    if (e.key === 'ArrowDown') {
      if (recallIndex.current === null) return; // not recalling; default caret
      e.preventDefault();
      if (recallIndex.current < inputHistory.length - 1) {
        recallIndex.current += 1;
        setInput(inputHistory[recallIndex.current]);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        });
      } else {
        // past newest: cancel recall + restore the saved draft
        recallIndex.current = null;
        setInput(draftRef.current);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (recallIndex.current === null) return; // not recalling; default
      e.preventDefault();
      recallIndex.current = null;
      setInput(draftRef.current);
      return;
    }
  };

  // Save an assistant message as a case response (ports ChatPanel.handleSave).
  const handleSave = useCallback(async (idx: number) => {
    const m = messages[idx];
    if (!m || savingIdx !== null) return;
    setSavingIdx(idx);
    try {
      await saveChatResponse(caseData.id, m.content, m.model_used, m.path_used);
      addToast('Response saved to case', 'success');
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(e?.response?.data?.detail || 'Failed to save response', 'error');
    } finally {
      setSavingIdx(null);
    }
  }, [messages, savingIdx, caseData.id, addToast]);

  // "Evaluate vs public data" (ports ChatPanel.handleEvaluateVsPublic) --
  // trigger a fresh public-data lookup, poll until COMPLETED/FAILED, then
  // run the LLM eval + post the discrepancy list as an assistant message
  // with citations.
  const handleEvaluateVsPublic = useCallback(async () => {
    if (evaluating || sending) return;
    const ticker = caseData.ticker || null;
    const issuerName = caseData.issuer_name || caseData.entity_name || null;
    const queryValue = ticker || issuerName || '';
    if (!queryValue) {
      addToast('No issuer name or ticker to look up for this case', 'error');
      return;
    }
    const queryType = ticker ? 'ticker' : 'name';
    setEvaluating(true);
    setEvalPhase('Gathering public data...');
    try {
      const started = await triggerPublicLookup(caseData.id, queryType, queryValue);
      const runId = started.run_id;
      let status = started.status;
      const deadline = Date.now() + 180_000;
      while (status === 'RUNNING' && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const run = await getPublicDataRun(caseData.id, runId);
        status = run.status;
        if (run.results_count != null) {
          setEvalPhase(`Gathering public data... (${run.results_count} results)`);
        }
      }
      if (status !== 'COMPLETED') {
        pushSystem(
          status === 'FAILED'
            ? 'Public data lookup failed. See the Public Data tab for details.'
            : 'Public data lookup timed out. Try again or check the Public Data tab.',
          { scope: 'public' },
        );
        return;
      }
      setEvalPhase('Evaluating case claims vs public data...');
      const resp = await evaluateVsPublic(caseData.id, runId);
      const body = discrepanciesBlock(resp.discrepancies, resp.summary);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: body,
          path_used: resp.path_used,
          model_used: resp.model_used,
          run_id: resp.run_id,
          citations: resp.citations,
          scope: 'public',
        },
      ]);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(e?.response?.data?.detail || 'Evaluate-vs-public request failed', 'error');
      pushSystem(`Error: ${e?.response?.data?.detail || 'Evaluate-vs-public request failed'}`, { scope: 'public' });
    } finally {
      setEvaluating(false);
      setEvalPhase('');
    }
  }, [evaluating, sending, caseData.id, caseData.ticker, caseData.issuer_name, caseData.entity_name, addToast, pushSystem]);

  const removeAttachment = (id: string) => setPendingAttachments((prev) => prev.filter((d) => d.id !== id));

  return (
    <div
      className="flex flex-col h-full"
      data-testid="chat-tab"
      onDragOver={onChatDragOver}
      onDrop={onDrop}
    >
      {/* Header: scope + model-path selectors, Run-review cluster, Evaluate */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 flex-wrap" data-testid="chat-header">
        <span className="text-xs font-medium text-slate-500 mr-0.5">Scope:</span>
        {SCOPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setScope(opt.value)}
            title={opt.note}
            className={`text-xs px-2 py-0.5 rounded-full border font-medium transition ${
              scope === opt.value
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-slate-600 border-slate-300 hover:border-primary-400'
            }`}
            data-testid={`chat-scope-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-[11px] text-slate-400 hidden md:inline-flex items-center gap-1" title={activeScopeNote}>
          <Shield className="h-3 w-3" /> {activeScopeNote}
        </span>
        <span className="text-xs font-medium text-slate-500 ml-2 mr-0.5">Model:</span>
        {MODEL_PATH_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setModelPath(opt.value)}
            title={opt.note}
            className={`text-xs px-2 py-0.5 rounded-full border font-medium transition ${
              modelPath === opt.value
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-slate-600 border-slate-300 hover:border-purple-400'
            }`}
            data-testid={`chat-model-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {/* Run review cluster -- mirrors the Overview + old CaseDetail.tsx
              pattern. Reuses handlers lifted into CaseDetailPane. */}
          {reviewInProgress && onCancelReview && (
            <button
              onClick={onCancelReview}
              disabled={cancellingReview}
              className="text-xs px-2 py-0.5 rounded-full border font-medium bg-white text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1"
              title="Cancel the in-progress review run"
              data-testid="chat-cancel-review"
            >
              {cancellingReview ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
              Cancel Review
            </button>
          )}
          {hasReview ? (
            onRunReview && (
              <button
                onClick={onRunReview}
                disabled={reviewLoading || reviewInProgress}
                className="text-xs px-2 py-0.5 rounded-full border font-medium bg-white text-slate-600 border-slate-300 hover:border-primary-400 disabled:opacity-50 inline-flex items-center gap-1"
                title="Re-run the rubric review without pulling new public data"
                data-testid="chat-rerun-review"
              >
                {reviewLoading || reviewInProgress ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Re-run Review
              </button>
            )
          ) : (
            <>
              {onReReview && (
                <button
                  onClick={onReReview}
                  disabled={rereviewLoading || reviewInProgress}
                  className="text-xs px-2 py-0.5 rounded-full border font-medium bg-primary-600 text-white border-primary-600 hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-1"
                  title="Pull public data (EDGAR, OTC, FINRA, OFAC, etc.) then run the rubric review"
                  data-testid="chat-pull-and-review"
                >
                  {rereviewLoading || reviewInProgress ? <Loader2 className="h-3 w-3 animate-spin" /> : <Database className="h-3 w-3" />}
                  Pull &amp; Review
                </button>
              )}
              {onRunReview && (
                <button
                  onClick={onRunReview}
                  disabled={reviewLoading || reviewInProgress}
                  className="text-xs px-2 py-0.5 rounded-full border font-medium bg-white text-slate-600 border-slate-300 hover:border-primary-400 disabled:opacity-50 inline-flex items-center gap-1"
                  title="Run the rubric review without pulling new public data"
                  data-testid="chat-run-review"
                >
                  {reviewLoading || reviewInProgress ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  Run Review
                </button>
              )}
            </>
          )}
          {/* Evaluate vs public data (ported from ChatPanel) */}
          <button
            onClick={handleEvaluateVsPublic}
            disabled={evaluating || sending}
            title="Trigger a fresh public-data lookup + compare case claims against it"
            className="text-xs px-2 py-0.5 rounded-full border font-medium bg-white text-slate-600 border-slate-300 hover:border-primary-400 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="chat-evaluate-vs-public"
          >
            {evaluating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Evaluate vs public
          </button>
        </div>
      </div>
      {/* Chat thread -- drag handlers are NOT attached here; they live on
          the input-bar drop zone below so the message list never re-renders
          on drag state changes (dir-057). */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 relative"
        data-testid="chat-thread"
      >
        {loading && <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>}
        {error && !loading && <div className="text-sm text-red-600 text-center">{error}</div>}
        {!loading && messages.length === 0 && (
          <div className="text-center text-slate-400 py-12 text-sm max-w-md mx-auto space-y-1" data-testid="chat-empty-state">
            <p>
              {caseData.type === 'comparison'
                ? 'Upload an original document and one or more revised versions to compare.'
                : 'Drag deposit documents here to start the review. The system will automatically generate a deficiency list.'}
            </p>
            <p className="text-xs text-slate-400">
              Also accepts PII-stripped / data-washed documents (with <span className="font-mono">REDACTED-&lt;key&gt;</span> tokens) -- drop or attach one to repopulate the original PII from this case's tag map.
            </p>
          </div>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                  isUser
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}
                data-testid={`chat-msg-${i}`}
              >
                {m.warnings && m.warnings.length > 0 && (
                  <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 rounded-md px-2 py-1 mb-1 text-xs">
                    <AlertTriangle size={12} /> {m.warnings[0]}
                  </div>
                )}
                {m.unknown_tags && m.unknown_tags.length > 0 && !m.warnings && (
                  <div className="flex items-center gap-1.5 text-amber-700 text-xs mb-1">
                    <AlertTriangle size={12} /> Unknown tag(s): {m.unknown_tags.join(', ')}
                  </div>
                )}
                {isUser ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : m._pending ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 size={14} className="animate-spin" />
                    {m._pendingKind === 'ai_chat'
                      ? 'Thinking...'
                      : m.content || 'Processing...'}
                  </div>
                ) : (
                  <MarkdownRenderer content={m.content} />
                )}
                {m.result_link && (
                  <div className="mt-1 text-xs">
                    <span className="text-primary-600">See {m.result_link} tab.</span>
                  </div>
                )}
                {!isUser && m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                  <div className="mt-1.5">
                    <button
                      onClick={() => setSourcesOpen(prev => ({ ...prev, [i]: !prev[i] }))}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      data-testid={`chat-sources-toggle-${i}`}
                    >
                      {sourcesOpen[i] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      Data sources ({m.citations.length})
                    </button>
                    {sourcesOpen[i] && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {m.citations.map((c, j) => (
                          <span
                            key={j}
                            title={c.snippet || c.ref}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600"
                          >
                            {citationIcon(c.type)}
                            {c.type === 'document' && c.filename
                              ? `${c.filename}${c.washed ? ' (washed)' : ''}`
                              : c.ref}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!isUser && m.role === 'assistant' && (m.path_used || m.model_used) && (
                  <div className="mt-1 text-[11px] text-slate-400 flex items-center gap-2">
                    {m.path_used && (
                      <span className="inline-flex items-center gap-1">
                        <Shield className="h-3 w-3" /> path: {m.path_used}
                      </span>
                    )}
                    {m.model_used && <span>model: {m.model_used}</span>}
                  </div>
                )}
                {!isUser && (
                  <div className="mt-1.5 flex justify-end gap-1.5">
                    <button
                      onClick={() => handleCopy(i)}
                      title="Copy response to clipboard"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      data-testid={`chat-copy-${i}`}
                    >
                      <Copy className="h-3 w-3" /> {copiedIdx === i ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={() => handleExport(i)}
                      title="Download response as a .md file"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      data-testid={`chat-export-${i}`}
                    >
                      <Download className="h-3 w-3" /> Export .md
                    </button>
                    <button
                      onClick={handleExportDocx}
                      disabled={exportingDocx}
                      title="Download the whole conversation as a Word (.docx) file"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-50"
                      data-testid={`chat-export-docx-${i}`}
                    >
                      {exportingDocx ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}{' '}
                      Export chat .docx
                    </button>
                    {m.role === 'assistant' && (
                      <button
                        onClick={() => handleSave(i)}
                        disabled={savingIdx === i}
                        title="Save this response to the case"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-50"
                        data-testid={`chat-save-${i}`}
                      >
                        {savingIdx === i ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Bookmark className="h-3 w-3" />
                        )}
                        Save
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-slate-500 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Thinking...
            </div>
          </div>
        )}
        {evaluating && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-slate-500 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> {evalPhase || 'Working...'}
            </div>
          </div>
        )}
      {/* No drag overlay over the message list -- the drop-active visual is
          a CSS class swap on the input-bar drop zone below (dir-057). */}
      </div>

      {/* Pending attachments */}
      {pendingAttachments.length > 0 && (
        <div className="px-4 pt-2 flex flex-wrap gap-2">
          {pendingAttachments.map((d) => (
            <span key={d.id} className="inline-flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1 text-xs text-slate-700">
              <FileText size={12} /> {d.original_filename}
              <button onClick={() => removeAttachment(d.id)} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Input bar -- this is the drop zone. Drag handlers are scoped here
          (small isolated element, NOT the chat thread). Drop-active visual is
          a border + bg class swap with transition-colors; no overlay over the
          message list, so the messages never re-render on drag state (dir-057). */}
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={`border-t transition-colors ${
          isDragging
            ? 'border-primary-400 bg-primary-50'
            : 'border-slate-200 bg-white'
        }`}
        data-testid="chat-drop-zone"
      >
      <div className="p-3 flex items-end gap-2">
        <input ref={fileInputRef} type="file" multiple
          accept=".pdf,.doc,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,text/markdown"
          className="hidden" onChange={onAttach} data-testid="file-input" />
        <button onClick={() => fileInputRef.current?.click()} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Attach files" data-testid="attach-btn">
          <Paperclip size={18} />
        </button>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask... (Up-arrow recalls history)"
          rows={1}
          className="flex-1 resize-none px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors max-h-32"
          data-testid="chat-input"
        />
        <button onClick={onSend} disabled={sending || !input.trim()} className="btn-primary !px-3 !py-2" data-testid="send-btn">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
      </div>

      <div className="flex items-start gap-2 px-3 pb-2 text-xs text-slate-500 bg-white" data-testid="ai-disclaimer">
        <AlertCircle size={12} className="shrink-0 mt-0.5" />
        <span>AI-generated — AI can make mistakes. Verify before relying.</span>
      </div>
    </div>
  );
}